import {
  ArrowRightIcon,
  ChevronDownIcon,
  QuestionOutlineIcon,
} from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useProfile } from "../../shared/hooks/useProfile";

/**
 * GlobalHeader:
 * - Animated accent bar + dynamic route title
 * - Adaptive frosted glass effect (scroll / hover / sidebar state)
 * - Avatar menu (Help, Logout)
 */
const GlobalHeader = ({ collapsed }) => {
  const { data: profile, isLoading } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // derive page title
  const routeTitle = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.startsWith("/dashboard")) return "Dashboard";
    if (path.startsWith("/applications")) return "Application Catalog";
    if (path.startsWith("/me/masterresume")) return "Master Resume";
    if (path.startsWith("/me/work")) return "Work Experience";
    if (path.startsWith("/me/education")) return "Education & Projects";
    if (path.startsWith("/me/skills")) return "Summary & Skills";
    if (path.startsWith("/me")) return "My Profile";
    return "Welcome";
  }, [location.pathname]);

  // dynamic frosted background
  const lightBase = collapsed
    ? "rgba(245, 245, 245, 0.85)"
    : "rgba(255, 255, 255, 0.8)";
  const lightHover = collapsed
    ? "rgba(250, 250, 250, 0.95)"
    : "rgba(255, 255, 255, 0.9)";
  const darkBase = collapsed
    ? "rgba(23, 25, 35, 0.75)"
    : "rgba(26, 32, 44, 0.6)";
  const darkHover = collapsed
    ? "rgba(23, 25, 35, 0.85)"
    : "rgba(26, 32, 44, 0.7)";

  const bg = useColorModeValue(
    hovered || scrolled ? lightHover : lightBase,
    hovered || scrolled ? darkHover : darkBase,
  );
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  // motion variant for the orange bar
  const barVariants = {
    initial: { height: 0, opacity: 0, y: 6 },
    animate: { height: 24, opacity: 1, y: 0 },
    exit: { height: 0, opacity: 0, y: -6 },
  };

  return (
    <Flex
      as="header"
      position="sticky"
      top="0"
      zIndex="20"
      px={6}
      py={3}
      align="center"
      justify="space-between"
      backdropFilter="saturate(180%) blur(10px)"
      bg={bg}
      borderBottom={scrolled || hovered ? "1px solid" : "1px solid transparent"}
      borderColor={borderColor}
      boxShadow={scrolled ? "md" : "none"}
      transition="all 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Left: Animated Title Block */}
      <HStack spacing={3} align="center">
        <AnimatePresence mode="wait">
          <motion.div
            key={routeTitle}
            variants={barVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <Box w="4px" h="24px" bg="orange.500" borderRadius="full" />
          </motion.div>
        </AnimatePresence>

        <motion.div
          key={`${routeTitle}-text`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Text
            fontSize="lg"
            fontWeight="regular"
            color={useColorModeValue("gray.800", "gray.100")}
          >
            {routeTitle}
          </Text>
        </motion.div>
      </HStack>

      {/* Right: Avatar + Dropdown */}
      <Menu>
        <MenuButton>
          <HStack 
            spacing={3} 
            cursor="pointer" 
            px={3}
            py={2}
            borderRadius="lg"
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{ 
              bg: useColorModeValue("gray.50", "gray.700"),
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
            }}
          >
            <Avatar
              size="sm"
              name={profile?.name}
              src={profile?.avatar}
              bg="gray.200"
            />
            <Text fontWeight="medium" color="gray.700">
              {isLoading ? "Loading..." : profile?.name || "User"}
            </Text>
            <Icon as={ChevronDownIcon} color="gray.500" />
          </HStack>
        </MenuButton>

        <MenuList 
          shadow="xl" 
          borderColor={borderColor}
          borderRadius="xl"
          py={2}
          minW="200px"
          bg={useColorModeValue("white", "gray.800")}
        >
          <MenuItem
            icon={<QuestionOutlineIcon color="purple.500" />}
            onClick={() => navigate("/help")}
            _hover={{ bg: useColorModeValue("purple.50", "purple.900"), color: "purple.600" }}
            py={3}
            borderRadius="lg"
            mx={2}
            fontWeight="medium"
          >
            Help & Support
          </MenuItem>

          <Divider my={2} />

          <MenuItem
            icon={<ArrowRightIcon color="red.400" />}
            onClick={handleLogout}
            _hover={{ bg: useColorModeValue("red.50", "red.900"), color: "red.600" }}
            py={3}
            borderRadius="lg"
            mx={2}
            fontWeight="medium"
          >
            Logout
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

export default GlobalHeader;
